import { useState, useRef } from "react";
import Icon from "@Components/ui/icon";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { ZipCodePopUpsProps } from "@Types/ZipCodePopUps";
import { getItem, removeItem } from "@Utils/browserStorage";
import { isCanadianLocale } from "@Utils/locale";
import {
  useLaunchZipCodePopUp,
  useOnClickOutside,
} from "@wff/hooks/useActionBasedOnStorage";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import {
  setZipCodeHandler,
  toggleZipCodePopUp,
  ZipCodeSelector,
} from "@wff/store/zipcodeSlice";
import InitialLayout from "./layouts/InitialLayout";
import WeDeliverLayout from "./layouts/WeDeliverLayout";
import WeDontDeliverLayout from "./layouts/WeDontDeliverLayout";
import classes from "./ZipCodePopUp.module.scss";

export default function ZipCodePopUp({
  initialTitle,
  initialMessage,
  initialInputPlaceholder,
  initialButtonLabel,
  deliverTitle,
  deliverMessage,
  deliverButtonLabel,
  deliverLocationIcon,
  deliverChangePostalCodeText,
  noDeliverTitle,
  noDeliverMessage,
  noDeliverContinueButtonLabel,
  noDeliverNotifyButtonLabel,
  noDeliverLocationIcon,
  noDeliverChangePostalCodeText,
  noDeliveryAndPickupMessage,
  noDeliveryAndPickupContinueButtonLabel,
  onlyDeliveryAvailableTitle,
}: ZipCodePopUpsProps) {
  const [currentLayout, setCurrentLayout] = useState("initial");
  const {
    zipcode,
    zipcodePopupOpen,
    prevZipcode,
    nearestPickupLocation,
    isPickupAvailable,
  } = useAppSelector(ZipCodeSelector);
  const [isPopUpClosed, setIsPopUpClosed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const currentLocale = useAppSelector(localeSelector);
  const isCanadianUser = isCanadianLocale(currentLocale);
  const hideCloseButton = currentLayout === "initial" ? prevZipcode : "";

  const closeZipCodePopUp = () => {
    setCurrentLayout("initial");
    if (zipcodePopupOpen && isCanadianUser) {
      setIsPopUpClosed(true);
      return dispatch(toggleZipCodePopUp());
    }
  };

  const handleToggle = () => {
    const zipCodeAvailabilityInLocalStorageForCA = getItem(
      STORAGE_KEYS.CUSTOMER_ZIP_AVAILABILITY_CA,
      STORAGE_TYPES.LOCAL
    );

    setCurrentLayout("initial");

    if (zipcodePopupOpen && isCanadianUser) {
      saveZipcode({
        inputZipcode: zipcode,
        availability: Boolean(zipCodeAvailabilityInLocalStorageForCA),
      });
      setIsPopUpClosed(true);
      return dispatch(toggleZipCodePopUp());
    }

    if (!isCanadianUser || zipCodeAvailabilityInLocalStorageForCA) {
      isCanadianUser && !isPopUpClosed && dispatch(toggleZipCodePopUp());
      !isCanadianUser && dispatch(toggleZipCodePopUp());
      if (getItem(STORAGE_KEYS.TIMER_TRIGGERED_CA, STORAGE_TYPES.LOCAL)) {
        removeItem(STORAGE_KEYS.TIMER_TRIGGERED_CA, STORAGE_TYPES.LOCAL);
      }
    }
  };

  const handleLayoutSwitchInitial = () => {
    setCurrentLayout("initial");
    removeItem(STORAGE_KEYS.CUSTOMER_ZIP, STORAGE_TYPES.LOCAL);
    // NOTE: code commented because it was manually setting the postal code empty.
    // Ticket Reference:https://wild-fork-canada.atlassian.net/browse/CE-1320
    // and requirement was to persist the previous postal code.
    // saveZipcode({ inputZipcode: "", availability: false });
  };

  const saveZipcode = ({
    inputZipcode,
    availability,
  }: {
    inputZipcode: string;
    availability: boolean;
  }) => {
    const zipcodeInfo = {
      zipcode: inputZipcode,
      availability,
      ...(isCanadianUser && { prevZipcode: zipcode }),
    };
    dispatch(setZipCodeHandler(zipcodeInfo));
  };

  const initialLayoutProps = {
    initialTitle,
    initialMessage,
    initialInputPlaceholder,
    initialButtonLabel,
    zipcode,
    saveZipcode,
    switchLayout: setCurrentLayout,
  };

  const weDeliverProps = {
    deliverTitle,
    deliverMessage,
    deliverButtonLabel,
    deliverLocationIcon,
    deliverChangePostalCodeText,
    zipcode,
    handleToggle,
    closeZipCodePopUp,
    switchLayout: handleLayoutSwitchInitial,
    nearestPickupLocation,
    isPickupAvailable,
    onlyDeliveryAvailableTitle,
  };

  const weDontDeliverProps = {
    noDeliverTitle,
    noDeliverMessage,
    noDeliverContinueButtonLabel,
    noDeliverNotifyButtonLabel,
    noDeliverLocationIcon,
    noDeliverChangePostalCodeText,
    zipcode,
    handleToggle,
    closeZipCodePopUp,
    switchLayout: handleLayoutSwitchInitial,
    nearestPickupLocation,
    isPickupAvailable,
    noDeliveryAndPickupMessage,
    noDeliveryAndPickupContinueButtonLabel,
  };

  const layouts: { [key: string]: any } = {
    initial: <InitialLayout {...initialLayoutProps} />,
    weDeliver: <WeDeliverLayout {...weDeliverProps} />,
    weDontDeliver: <WeDontDeliverLayout {...weDontDeliverProps} />,
  };

  const activeLayout = layouts[currentLayout];

  useLaunchZipCodePopUp({ zipcodePopupOpen, zipcode, handleToggle });
  useOnClickOutside({ ref, isCanadianUser, hideCloseButton, handleToggle });
  /**
   *
   * For CA users: Popup close button is not shown for change postal code scenario only
   * For US users: it work as previous
   */
  const popUpCloseBtnRender = () => {
    if (!zipcode && !isCanadianUser) return;

    return (
      <button
        data-testid="zipcode-popup--close"
        className={classes["trade-area__close-modal-button"]}
        onClick={closeZipCodePopUp}
      >
        <Icon iconName="close" />
      </button>
    );
  };

  return (
    <>
      {zipcodePopupOpen ? (
        <div className={`${classes["modal"]}`}>
          <div className={classes["modal__overlay"]}>
            <div ref={ref} className={classes["modal__content"]}>
              <section
                className={classes["trade-area"]}
                data-testid="zipcode-popup"
              >
                {popUpCloseBtnRender()}
                {activeLayout}
              </section>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
